import styled, { css } from "styled-components";

export const Page = styled.main`
    box-sizing: border-box;
    min-height: 100vh;
    padding: 16px 5px;
    background: ${({theme}) => theme.colors.white};
    color: ${({theme}) => theme.colors.nurseryInk};
    font: ${({theme}) => theme.fonts.calculator.widgetBody};

    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }
`;

export const Calculator = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 780px;
  margin: 0 auto;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.nurseryLine};
  border-radius: 16px;
`;

export const Header = styled.header`
  padding: 24px 28px 20px;

  @media (max-width: 419px) {
    padding: 24px 12px 20px;
  }
`;

export const Eyebrow = styled.p`
  margin-bottom: 6px;
  color: ${({ theme }) => theme.colors.marmelade};
  font: ${({ theme }) => theme.fonts.calculator.widgetEyebrow};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const Title = styled.h1`
  font: ${({ theme }) => theme.fonts.calculator.widgetTitle};
  letter-spacing: -0.01em;
`;

export const Body = styled.div``;

export const Inputs = styled.div`
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 32px;
  align-items: start;
  padding: 4px 28px 22px;

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }

  @media (max-width: 419px) {
    padding: 4px 12px 22px;
  }
`;

const fieldStyles = css`
  min-width: 0;
  margin: 0 0 24px;
  padding: 0;
  border: 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const Field = styled.div`
  ${fieldStyles}
`;

export const Fieldset = styled.fieldset`
  ${fieldStyles}
`;

const fieldLabelStyles = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px;
  font: ${({ theme }) => theme.fonts.calculator.widgetLabel};
`;

export const FieldLabel = styled.label`
  ${fieldLabelStyles}

  > span {
    min-width: 0;
  }
`;

export const FieldLegend = styled.legend`
  ${fieldLabelStyles}
`;

export const FieldValue = styled.output`
  flex-shrink: 0;
  font: ${({ theme }) => theme.fonts.calculator.widgetFieldValue};
  font-variant-numeric: tabular-nums;
`;

export const Range = styled.input`
  width: 100%;
  height: 6px;
  margin: 0;
  appearance: none;
  background: ${({ theme }) => theme.colors.nurseryLine};
  border-radius: 3px;
  cursor: pointer;

  &::-webkit-slider-thumb {
    width: 26px;
    height: 26px;
    appearance: none;
    background: ${({ theme }) => theme.colors.marmelade};
    border: 3px solid ${({ theme }) => theme.colors.white};
    border-radius: 50%;
    box-shadow: 0 1px 5px ${({ theme }) => theme.colors.shadow};
    cursor: grab;
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: ${({ theme }) => theme.colors.marmelade};
    border: 3px solid ${({ theme }) => theme.colors.white};
    border-radius: 50%;
    box-shadow: 0 1px 5px ${({ theme }) => theme.colors.shadow};
    cursor: grab;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.marmelade};
    outline-offset: 4px;
  }
`;

export const ScaleRange = styled(Range)`
  &::-webkit-slider-thumb {
    background: ${({ theme }) => theme.colors.blueberry};
  }

  &::-moz-range-thumb {
    background: ${({ theme }) => theme.colors.blueberry};
  }

  &:focus-visible {
    outline-color: ${({ theme }) => theme.colors.blueberry};
  }
`;

export const RangeHints = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  color: ${({ theme }) => theme.colors.nurseryInkSoft};
  font: ${({ theme }) => theme.fonts.calculator.widgetPillSub};
`;

export const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Pill = styled.button`
  padding: 8px 14px;
  appearance: none;
  background: ${({ theme }) => theme.colors.white};
  border: 1.5px solid ${({ theme }) => theme.colors.nurseryLine};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.nurseryInk};
  cursor: pointer;
  font: ${({ theme }) => theme.fonts.calculator.widgetPill};
  text-align: left;
  transition:
    border-color 0.15s,
    background 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.blueberry};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.blueberry};
    outline-offset: 2px;
  }

  &[aria-pressed="true"] {
    background: ${({ theme }) => theme.colors.blueberry};
    border-color: ${({ theme }) => theme.colors.blueberry};
  }
`;

export const PillSub = styled.span`
  display: block;
  font: ${({ theme }) => theme.fonts.calculator.widgetPillSub};
  opacity: 0.75;
`;

export const Results = styled.section`
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  padding: 8px 28px 28px;

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }

  @media (max-width: 419px) {
    padding: 8px 12px 28px;
  }
`;

export const CardValue = styled.p`
  color: ${({ theme }) => theme.colors.marmelade};
  font: ${({ theme }) => theme.fonts.calculator.widgetValue};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
`;

type CardVariant = "parent" | "nursery" | "scale";

export const Card = styled.div<{ $variant: CardVariant }>`
  min-width: 0;
  padding: 20px 22px;
  background: ${({ theme }) => theme.colors.white};
  border: 2px solid
    ${({ $variant, theme }) =>
      $variant === "parent" ? theme.colors.peach : theme.colors.blueberry};
  border-radius: 14px;

  ${({ $variant }) =>
    $variant === "parent" &&
    css`
      grid-column: 1 / -1;
    `}

  ${({ $variant }) =>
    $variant !== "parent" &&
    css`
      ${CardValue} {
        font: ${({ theme }) => theme.fonts.calculator.widgetValueSmall};
      }
    `}
`;

export const ParentGrid = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  gap: 24px;
  align-items: center;

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

export const ParentYear = styled.div`
  padding-left: 24px;
  border-left: 1px solid ${({ theme }) => theme.colors.nurseryLine};

  @media (max-width: 560px) {
    padding-top: 14px;
    padding-left: 0;
    border-top: 1px solid ${({ theme }) => theme.colors.nurseryLine};
    border-left: 0;
  }
`;

export const CardLabel = styled.p`
  margin-bottom: 2px;
  color: ${({ theme }) => theme.colors.nurseryInk};
  font: ${({ theme }) => theme.fonts.calculator.widgetLabel};
`;

export const CardPer = styled.span`
  color: ${({ theme }) => theme.colors.nurseryInkSoft};
  font: ${({ theme }) => theme.fonts.calculator.widgetPer};
  letter-spacing: 0;
`;

export const Split = styled.div`
  margin-top: 16px;
`;

export const SplitBar = styled.div`
  display: flex;
  height: 30px;
  overflow: hidden;
  border-radius: 8px;
`;

export const SplitSegment = styled.div<{ $variant: "pay" | "save" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${({ $variant, theme }) =>
    $variant === "pay" ? theme.colors.blueberry : theme.colors.marmelade};
  color: ${({ $variant, theme }) =>
    $variant === "pay" ? theme.colors.nurseryInk : theme.colors.white};
  font: ${({ theme }) => theme.fonts.calculator.widgetSegment};
  white-space: nowrap;
  transition: width 0.35s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const SplitLegend = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 7px;
  color: ${({ theme }) => theme.colors.nurseryInkSoft};
  font: ${({ theme }) => theme.fonts.calculator.widgetSmall};

  strong {
    color: ${({ theme }) => theme.colors.nurseryInk};
    font: ${({ theme }) => theme.fonts.calculator.widgetSmallBold};
    font-variant-numeric: tabular-nums;
  }
`;

export const CardNote = styled.p`
  margin-top: 10px;
  color: ${({ theme }) => theme.colors.nurseryInkSoft};
  font: ${({ theme }) => theme.fonts.calculator.widgetSmall};

  strong {
    color: ${({ theme }) => theme.colors.nurseryInk};
    font: ${({ theme }) => theme.fonts.calculator.widgetSmallBold};
    font-variant-numeric: tabular-nums;
  }
`;

export const ScaleControl = styled.div`
  margin: 4px 0 14px;
`;

export const ScaleFamilies = styled.label`
  min-width: 0;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;
  font: ${({ theme }) => theme.fonts.calculator.widgetLabel};

  > span {
    min-width: 0;
  }
`;

export const ScaleCount = styled.output`
  flex-shrink: 0;
  font: ${({ theme }) => theme.fonts.calculator.widgetScaleCount};
  font-variant-numeric: tabular-nums;
`;

export const Footer = styled.footer`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 28px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.nurseryLine};

  @media (max-width: 419px) {
    padding: 16px 12px 20px;
  }
`;

export const FooterNote = styled.p`
  max-width: 470px;
  color: ${({ theme }) => theme.colors.nurseryInkSoft};
  font: ${({ theme }) => theme.fonts.calculator.widgetTiny};
`;

export const FooterBrand = styled.a`
  color: ${({ theme }) => theme.colors.nurseryInk};
  font: ${({ theme }) => theme.fonts.calculator.widgetLabel};
  text-decoration: none;
  white-space: nowrap;

  span {
    color: ${({ theme }) => theme.colors.marmelade};
  }

  &:hover span {
    text-decoration: underline;
  }
`;
